import { SensorsApi, ApiClient } from '../generated/src';
import API_CONFIG from '../config/apiConfig';
import { CONNECTION_STATUS } from '../context/enums';

/**
 * Polling service for Fire Bridge sensor data
 * Handles continuous polling of the Fire Bridge API and error management
 */
class PollingService {
  constructor() {
    this.apiClient = null;
    this.sensorsApi = null;
    this.pollingInterval = null;
    this.isPolling = false;
    this.callbacks = new Set();
    this.errorCallbacks = new Set();
    this.connectionStatus = CONNECTION_STATUS.DISCONNECTED;
    this.lastError = null;
    this.retryCount = 0;
    this.lastSuccessfulPoll = null;
    this.tokenGetter = null;
    this.currentFireControllerId = null;
  }

  /**
   * Initialize the API client with the given base URL
   * @param {string} baseUrl - The Fire Bridge server base URL
   * @param {Function} getTokenFn - Function that returns the current authentication token.
   *                                Called before each API request to support dynamic token refresh.
   *                                For static tokens, use: () => 'your-static-token'
   */
  initialize(baseUrl, getTokenFn) {
    try {
      this.apiClient = new ApiClient(baseUrl);
      this.apiClient.timeout = API_CONFIG.TIMEOUT;

      // Store token getter function
      if (getTokenFn && typeof getTokenFn === 'function') {
        this.tokenGetter = getTokenFn;
      }

      this.sensorsApi = new SensorsApi(this.apiClient);
      this.connectionStatus = 'initialized';
      console.log(`PollingService initialized with base URL: ${baseUrl}`);
    } catch (error) {
      console.error('Failed to initialize PollingService:', error);
      this.connectionStatus = CONNECTION_STATUS.ERROR;
      this.lastError = error;
      throw error;
    }
  }

  /**
   * Set the current Fire Controller ID to poll status for
   * @param {string} fireControllerId - The Fire Controller ID to target
   */
  setFireControllerId(fireControllerId) {
    this.currentFireControllerId = fireControllerId;
  }

  /**
   * Set a function that returns the current authentication token
   * @param {Function} getTokenFn - Function that returns the current token
   */
  setTokenGetter(getTokenFn) {
    if (typeof getTokenFn !== 'function') {
      throw new Error('Token getter must be a function');
    }
    this.tokenGetter = getTokenFn;
    console.log('Token getter function updated');
  }

  /** Start polling for sensor data*/
  startPolling() {
    if (this.isPolling) {
      console.warn('Polling is already active');
      return;
    }

    if (!this.sensorsApi) {
      throw new Error('PollingService not initialized. Call initialize() first.');
    }

    this.isPolling = true;
    this.connectionStatus = 'connecting';
    console.log(`Starting polling with interval: ${API_CONFIG.POLLING_INTERVAL}ms`);

    this.pollData();

    this.pollingInterval = setInterval(() => {
      this.pollData();
    }, API_CONFIG.POLLING_INTERVAL);
  }

  /* Stop polling */
  stopPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
    this.isPolling = false;
    this.connectionStatus = CONNECTION_STATUS.DISCONNECTED;
    console.log('Polling stopped');
  }

  /** Perform a single data poll*/
  async pollData() {
    if (!this.sensorsApi) {
      this.handleError(new Error('API not initialized'));
      return;
    }

    try {
      this.connectionStatus = 'connecting';

      // Update token before each API call to support token refresh
      if (this.tokenGetter) {
        const currentToken = this.tokenGetter();
        if (currentToken) {
          this.apiClient.authentications['BearerAuth'].accessToken = currentToken;
        }
      }

      // Guard: require a selected Fire Controller before polling
      if (!this.currentFireControllerId) {
        console.warn('No fire controller selected — skipping poll');
        return;
      }

      // Call the API using Promise-based approach
      const data = await new Promise((resolve, reject) => {
        this.sensorsApi.getSpecifiedControllerStatus(this.currentFireControllerId, (error, data) => {
          if (error) {
            reject(error);
          } else {
            resolve(data);
          }
        });
      });

      this.connectionStatus = CONNECTION_STATUS.CONNECTED;
      this.lastError = null;
      this.retryCount = 0;
      this.lastSuccessfulPoll = new Date();

      // Enhanced logging for debugging empty data issues
      if (!data || Object.keys(data).length === 0) {
        console.warn('API returned empty or null data');
        console.warn('This might indicate:');
        console.warn('- Fire Bridge API is not receiving sensor updates');
        console.warn('- API is returning default/uninitialized data');
        console.warn('- Network issues with sensor data source');
      }

      this.callbacks.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error('Error in poll callback:', error);
        }
      });

    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Handle polling errors
   * @param {Error} error - The error that occurred
   */
  handleError(error) {
    console.error('Polling error:', error);

    this.lastError = error;
    this.retryCount++;

    // Check for authentication errors (401 Unauthorized or 403 Forbidden)
    if (error.status === 401 || error.status === 403) {
      console.error('Authentication error detected - user session expired');
      this.connectionStatus = CONNECTION_STATUS.ERROR;

      // Notify error callbacks about authentication failure
      this.errorCallbacks.forEach(callback => {
        try {
          callback(error, this.connectionStatus);
        } catch (callbackError) {
          console.error('Error in error callback:', callbackError);
        }
      });

      // Stop polling to prevent repeated failed requests
      this.stopPolling();

      // Import keycloakService dynamically to avoid circular dependencies
      import('../services/keycloakService').then(module => {
        console.error('Logging out user due to expired session');
        module.default.logout();
      }).catch(err => {
        console.error('Failed to logout user:', err);
      });

      return;
    }

    if (error.status === 0 || error.code === 'ECONNREFUSED') {
      this.connectionStatus = CONNECTION_STATUS.DISCONNECTED;
    } else {
      this.connectionStatus = CONNECTION_STATUS.ERROR;
    }

    this.errorCallbacks.forEach(callback => {
      try {
        callback(error, this.connectionStatus);
      } catch (callbackError) {
        console.error('Error in error callback:', callbackError);
      }
    });

    // Continue polling indefinitely - no max retry limit
    console.log(`Poll failed (attempt ${this.retryCount}). Will retry on next polling interval.`);
  }

  /**
   * Add a callback for successful data polls
   * @param {Function} callback - Function to call with poll data
   */
  onData(callback) {
    this.callbacks.add(callback);
    return () => this.callbacks.delete(callback); // Return unsubscribe function
  }

  /**
   * Add a callback for polling errors
   * @param {Function} callback - Function to call with error and connection status
   */
  onError(callback) {
    this.errorCallbacks.add(callback);
    return () => this.errorCallbacks.delete(callback); // Return unsubscribe function
  }

  /**
   * Get current connection status
   * @returns {string} Current connection status
   */
  getConnectionStatus() {
    return this.connectionStatus;
  }

  /**
   * Get last error
   * @returns {Error|null} Last error that occurred
   */
  getLastError() {
    return this.lastError;
  }

  /**
   * Get last successful poll timestamp
   * @returns {Date|null} Timestamp of last successful poll
   */
  getLastSuccessfulPoll() {
    return this.lastSuccessfulPoll;
  }

  /**
   * Check if currently polling
   * @returns {boolean} True if polling is active
   */
  getIsPolling() {
    return this.isPolling;
  }

  /** Force a manual poll (useful for testing or manual refresh)*/
  manualPoll() {
    if (!this.sensorsApi) {
      throw new Error('PollingService not initialized');
    }
    return this.pollData();
  }

  /**
   * Update the base URL and reinitialize
   * @param {string} baseUrl - New base URL
   * @param {Function} getTokenFn - Optional new token getter function (if not provided, uses existing)
   */
  updateBaseUrl(baseUrl, getTokenFn) {
    const wasPolling = this.isPolling;
    const existingTokenGetter = this.tokenGetter;

    if (wasPolling) {
      this.stopPolling();
    }

    this.initialize(baseUrl, getTokenFn || existingTokenGetter);

    if (wasPolling) {
      this.startPolling();
    }
  }
}

export const pollingService = new PollingService();
export default pollingService;
